import { add, round } from 'vend-number'
import { fetchApiJson } from '@vend/utilities'

/**
 * Client types for GET /api/2.0/invoicing/v1/invoices?tags=
 * (InvoicesByFiltersResponse contract).
 */

export interface InvoicePaymentSummary {
  id: string
  amount: string
  currency: string
  status: string
  paymentTypeName: string
  createdAt: string
  capturedAt?: string
  creditCard?: { last4: string; brand: string }
  bankAccount?: { last4: string }
}

export interface InvoiceByFilters {
  id: string
  invoiceNumber: string
  currency: string
  processingBalance: string
  outstandingBalance: string
  payments: InvoicePaymentSummary[]
}

export interface InvoicesByFiltersResponse {
  invoices: InvoiceByFilters[]
}

export const INVOICE_PAYMENT_STATUS_PROCESSING = 'processing'

export const buildInvoicesBySaleTagUrl = (saleId: string): string =>
  `/api/2.0/invoicing/v1/invoices?tags=${encodeURIComponent(
    `saleId:${saleId}`
  )}`

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isFiniteAmount = (value: unknown): boolean => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false
  }
  return Number.isFinite(Number(value))
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== ''

const isInvoicePayment = (value: unknown): boolean =>
  isPlainObject(value) &&
  isFiniteAmount(value.amount) &&
  isNonEmptyString(value.status)

const isInvoiceByFilters = (value: unknown): value is InvoiceByFilters =>
  isPlainObject(value) &&
  Array.isArray(value.payments) &&
  value.payments.every(isInvoicePayment)

const isInvoicesByFiltersResponse = (
  data: unknown
): data is InvoicesByFiltersResponse =>
  isPlainObject(data) &&
  Array.isArray(data.invoices) &&
  data.invoices.every(isInvoiceByFilters)

export const isAchPayment = (payment: InvoicePaymentSummary): boolean =>
  payment.bankAccount != null

export const isInTransitAchPayment = (
  payment: InvoicePaymentSummary
): boolean =>
  isAchPayment(payment) && payment.status === INVOICE_PAYMENT_STATUS_PROCESSING

export const getAchPayments = (
  invoices: InvoiceByFilters[]
): InvoicePaymentSummary[] =>
  invoices.flatMap(invoice => invoice.payments.filter(isAchPayment))

export const getInTransitAchPayments = (
  payments: InvoicePaymentSummary[]
): InvoicePaymentSummary[] => payments.filter(isInTransitAchPayment)

/** Sum of this sale's processing ACH payments, in dollars. */
export const sumInTransitAchAmount = (
  payments: InvoicePaymentSummary[]
): number =>
  getInTransitAchPayments(payments).reduce(
    (total, payment) => Number(round(add(total, payment.amount), 2)),
    0
  )

export const fetchInvoicesBySaleId = async (
  saleId: string
): Promise<InvoicesByFiltersResponse> => {
  const data = await fetchApiJson(buildInvoicesBySaleTagUrl(saleId))

  if (!isInvoicesByFiltersResponse(data)) {
    throw new Error('Invalid invoices-by-filters response')
  }

  return {
    invoices: data.invoices,
  }
}
